<template>
  <div class="c-file-list">
    <slot v-if="!listState.items?.length" name="empty">
      <div v-if="showNoData" class="no-data" :class="{ 'no-padding': noPadding }">
        <slot name="no-data">暂无文件</slot>
      </div>
    </slot>
    <div v-else class="file-items">
      <template v-for="(it, index) in listState.items">
        <slot name="item" v-bind="{ _index: index, ...it }">
          <div :key="index" class="file-item" :title="it.name">
            <!-- 增强属性（同时支持图片、视频、文件的展示、缩放、下载） -->
            <div v-if="isEnhance">
              <!-- 图片 -->
              <div v-if="getExt(it) === 'image'" class="image-item" :class="{ isDownloadable }">
                <c-image
                  v-bind="imageAttrs"
                  class="image"
                  :src="srcType === 'url' ? it.url : it.path"
                  :upload-type="uploadType"
                />
              </div>
              <!-- 视频 -->
              <div v-else-if="getExt(it) === 'video'" class="image-item video-item" :class="{ isDownloadable }">
                <c-video
                  v-bind="imageAttrs"
                  class="image"
                  :src="srcType === 'url' ? it.url : it.path"
                  :upload-type="uploadType"
                />
              </div>
              <!-- 文件 -->
              <div v-else class="file-list-item-btn" @click="handleLink(it)">
                <div class="label text-ellipsis" :style="{ maxWidth: maxItemWidth }">
                  {{ it.name }}
                </div>
              </div>

              <el-button
                v-if="!readonly"
                v-preventReclick
                class="btn-delete"
                type="info"
                icon="el-icon-close"
                circle
                @click="handleDelete(index)"
              />
            </div>

            <!-- 原逻辑 -->
            <template v-else>
              <div v-if="listType === 'image'" class="image-item" :class="{ isDownloadable }">
                <c-image
                  v-bind="imageAttrs"
                  class="image"
                  :src="srcType === 'url' ? it.url : it.path"
                  :upload-type="uploadType"
                />
                <div v-if="isDownloadable" class="action-mask flex-center">
                  <el-button
                    v-if="!readonly"
                    v-preventReclick
                    class="btn-image-download"
                    type="text"
                    icon="el-icon-bottom"
                    @click="handleLink(it)"
                  />
                </div>
              </div>
              <div v-else class="text-item">
                <div v-if="isDownloadable" class="file-list-item-btn" @click="handleLink(it)">
                  <div class="label text-ellipsis" :style="{ maxWidth: maxItemWidth }">{{ it.name }}</div>
                </div>
                <div v-else class="label text-ellipsis" :style="{ maxWidth: maxItemWidth }">{{ it.name }}</div>
              </div>

              <el-button
                v-if="!readonly"
                v-preventReclick
                class="btn-delete"
                type="info"
                icon="el-icon-close"
                circle
                @click="handleDelete(index)"
              />
            </template>
          </div>
        </slot>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { _, ref, watch, reactive, computed, fileUtil, useCurrentAppInstance } from '@zto/zpage'

import type { GenericFunction } from '@zto/zpage'
import type { UploadFileItem } from './types'

const props = withDefaults(
  defineProps<{
    modelValue?: Array<any> | string
    readonly?: boolean
    downloadable?: boolean
    enhance?: boolean // 增强属性
    noPadding?: boolean
    showNoData?: boolean
    withoutPostfix?: boolean
    maxItemWidth?: string
    srcType?: string // 'path' | 'url'
    listType?: string // 'image' | 'file'
    imageOptions?: Record<string, any> // 图片选项
    downloadMethod?: GenericFunction
    remoteDelete?: boolean // 远程删除
    immediateDelete?: boolean // 删除操作时，直接删除指定项
    params?: string // 额外的参数
    uploadType?: string
    extCustom?: (path: any) => void
  }>(),
  {
    srcType: 'path',
    showNoData: false,
    maxItemWidth: '200px',
    remoteDelete: true,
    immediateDelete: false,
    listType: 'file'
  }
)

const emit = defineEmits(['update:modelValue', 'delete', 'deleted'])

const app = useCurrentAppInstance()

const listState = reactive<{
  items: UploadFileItem[]
}>({
  items: []
})

const imageAttrs = computed(() => {
  const _attrs = { ...props.imageOptions }
  return _attrs
})

const isDownloadable = computed(() => {
  if (typeof props.downloadable === 'boolean') return props.downloadable
  if (props.listType === 'image') return false
  return true
})

// 增强属性
const isEnhance = computed(() => {
  if (typeof props.enhance === 'boolean') return props.enhance
  return false
})

// 计算后缀
const getExt = (it: any) => {
  // 如果有自定义获取后缀的方法
  if (props?.extCustom) {
    return props.extCustom(it.path)
  }
  const path = it.name
  const extIndex = path.lastIndexOf('.')

  let ext = ''
  if (extIndex >= 0) {
    ext = path.slice(extIndex + 1)?.toLowerCase() // 转小写
  }

  // 图片
  const imageExt = ['png', 'jpg', 'jpeg', 'jpe', 'jxr', 'tif', 'tiff', 'bmp', 'webp']
  // 视频
  const videoExt = ['mp4', '3gp', 'avi', 'wmv', 'mpeg', 'mpg', 'mov', 'flv', 'swf', 'qsv', 'kux', 'mpg', 'rm', 'ram']

  if (imageExt.includes(ext)) {
    return 'image'
  }

  if (videoExt.includes(ext)) {
    return 'video'
  }

  return 'file'
}

watch(
  () => props.modelValue,
  () => {
    resetFileItems(props.modelValue)
  },
  {
    immediate: true
  }
)

async function handleDelete(index: number) {
  const item = listState.items[index]

  if (item) {
    if (props.immediateDelete) await deleteItem(item)
    emit('delete', item, index, props.params)
  }
}

function handleLink(it: UploadFileItem) {
  innerDownloadMethod(it)
}

async function innerDownloadMethod(it: UploadFileItem) {
  const options = {
    filename: it.name,
    withoutPostfix: props.withoutPostfix
  }

  if (props.downloadMethod) {
    props.downloadMethod(it, options)
  } else {
    let url = it.url
    if (props.srcType === 'path' && it.path) {
      url = await fileUtil.getUrlByPath(it.path, { app, uploadType: props?.uploadType })
    }
    let pdfReg = /^.+(\.pdf|\.PDF)$/
    if (url && pdfReg.test(it.name)) {
      window.open(url)
      return
    }
    if (url) await fileUtil.download(url, options)
  }
}

async function deleteItem(item: UploadFileItem | number) {
  let index: any = undefined

  if (typeof item === 'number') {
    index = item
    item = listState.items[index]
  } else {
    index = listState.items.indexOf(item)
  }

  if (item && index >= 0) {
    if (props.remoteDelete && item.path) {
      await fileUtil.deleteFile(item.path, { app })
    }

    listState.items.splice(index, 1)
    emit('deleted', item)

    const val = getModelValue()
    emit('update:modelValue', val)
  }
}

function resetFileItems(val: any[] | string | undefined) {
  let items: any[] = []

  if (_.isString(val)) {
    items = val.split(',')
  } else if (val) {
    items = val
  }

  if (!items?.length) {
    listState.items = []
    return
  }

  listState.items = items
    .filter(it => !!it)
    .map((it: any) => {
      let item: any = it

      if (_.isString(it)) {
        if (props.srcType === 'url') {
          item = { url: it }
        } else {
          item = { path: it }
        }
      }

      const url = item.url

      let name = item.name
      let path = item.path

      if (!name) {
        let fileName = props.srcType === 'url' ? item.url : item.path
        const { fname } = fileUtil.parseFileName(fileName)
        name = fname
      }

      return { name, path, url }
    })
}

function getModelValue() {
  if (_.isString(props.modelValue)) {
    const items = (listState.items || []).map(it => {
      return props.srcType === 'url' ? it.url : it.path
    })

    return items.join()
  }
  return [...listState.items]
}
</script>

<style lang="scss" scoped>
.c-file-list {
  --image-size: 80px;

  display: inline-block;
  border-radius: 3px;

  .file-items {
    padding: 5px 10px 5px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .file-item {
    position: relative;
    display: inline-block;
    border-radius: 5px;
    margin-right: 10px;
    margin-bottom: 10px;
    background-color: #ccc;

    :deep(.el-button.disabled) {
      color: grey;
    }

    :deep(.el-button.btn-delete) {
      padding: 2px;
      border: 0;
      min-height: 10px;
    }

    .image-item {
      position: relative;
      border: 1px solid #dcdfe6;
      border-radius: 4px;

      .btn-image-download {
        color: white;
      }

      :deep(.c-image) {
        width: var(--image-size);
        height: var(--image-size);

        .el-image {
          width: var(--image-size);
          height: var(--image-size);
          cursor: pointer;
        }
      }

      &:hover {
        .action-mask {
          visibility: visible;
        }
      }

      .action-mask {
        position: absolute;
        top: 0;
        height: 100%;
        width: 100%;
        visibility: hidden;
        font-size: 18px;
        cursor: pointer;
        background: rgba($color: #000000, $alpha: 0.3);
        color: white;
      }
    }

    .video-item {
      width: 80px;
      .empty {
        width: 80px;
        height: 80px;
      }
    }

    .text-item {
      padding: 0 10px;
    }

    .btn-delete {
      position: absolute;
      top: -5px;
      right: -5px;
      padding: 0;
      width: 18px;
      height: 18px;
    }
  }
}

.no-data {
  padding: 10px;

  &.no-padding {
    padding: 0;
    display: inline-block;
  }
}

.file-list-item-btn {
  color: #3693ff;
  cursor: pointer;
  font-weight: 700;
}
</style>
